import React, { useState, useRef, useEffect, useCallback } from 'react';

interface CarouselItem {
  id: string;
  image: string;
  title: string;
}

interface CarouselSnapProps {
  items: CarouselItem[];
  className?: string;
  acceptedItems?: Set<string>;
  onItemAccept?: (itemId: string) => void;
  onItemUnaccept?: (itemId: string) => void;
  showAcceptButton?: boolean;
  autoProgressOnAccept?: boolean;
}

const CarouselSnap: React.FC<CarouselSnapProps> = ({ 
  items, 
  className = '', 
  acceptedItems = new Set(),
  onItemAccept,
  onItemUnaccept,
  showAcceptButton = false,
  autoProgressOnAccept = false
}) => {
  const [currentIndex, setCurrentIndex] = useState(0); // Start with first item active
  const [acceptCounters, setAcceptCounters] = useState<{[key: string]: number}>({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isScrollingProgrammatically = useRef(false);

  // Initial scroll to center the first item
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Small delay to ensure component is fully rendered
    const timer = setTimeout(() => {
      isScrollingProgrammatically.current = true; // Prevent detection during initial scroll
      
      // Start at the first item (far left position)
      const targetScrollLeft = 0;
      
      container.scrollTo({
        left: targetScrollLeft,
        behavior: 'auto' // No animation on initial load
      });
      
      // Reset flag after initial positioning
      setTimeout(() => {
        isScrollingProgrammatically.current = false;
      }, 100);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Simple scroll detection using useCallback to avoid stale closures
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    // Skip scroll detection during programmatic scrolling
    if (isScrollingProgrammatically.current) return;
    
    const scrollLeft = container.scrollLeft;
    const containerWidth = container.offsetWidth;
    const padding = 295;
    
    // Calculate breakpoints based on centering positions
    const slide1Center = 0; // First slide stays at 0
    const slide2Center = 590 + 40 - (containerWidth - 680) / 2 + padding;
    const slide3Center = 590 + 40 + 680 + 40 - (containerWidth - 680) / 2 + padding;
    
    // Find which slide we're closest to
    const distances = [
      Math.abs(scrollLeft - slide1Center),
      Math.abs(scrollLeft - slide2Center),
      Math.abs(scrollLeft - slide3Center)
    ];
    
    const newIndex = distances.indexOf(Math.min(...distances));
    
    if (newIndex !== currentIndex) {
      setCurrentIndex(newIndex);
    }
  }, [currentIndex]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleClick = (index: number) => {
    // Only proceed if we're actually changing slides
    if (index === currentIndex) return;
    
    // Set the current index to trigger re-render
    setCurrentIndex(index);
    
    // Set flag to prevent scroll detection during programmatic scroll
    isScrollingProgrammatically.current = true;
    
    // Scroll to clicked item
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const containerWidth = container.offsetWidth;
      const padding = 295; // px-[295px] from container
      
      // Calculate the center position for each slide
      let targetScrollLeft = 0;
      
      if (index === 0) {
        // First slide: already at left edge (0)
        targetScrollLeft = 0;
      } else if (index === 1) {
        // Second slide: need to scroll to center it
        // Position of second slide start: 590 (first slide) + 40 (gap)
        // To center the active slide (680px wide), we need:
        // slideStartPosition - (containerWidth - slideWidth) / 2 + padding
        const slideStartPosition = 590 + 40;
        const activeSlideWidth = 680;
        targetScrollLeft = slideStartPosition - (containerWidth - activeSlideWidth) / 2 + padding;
      } else {
        // Third slide: need to scroll to show it fully
        // Position of third slide start: 590 + 40 + 680 + 40
        const slideStartPosition = 590 + 40 + 680 + 40;
        const activeSlideWidth = 680;
        targetScrollLeft = slideStartPosition - (containerWidth - activeSlideWidth) / 2 + padding;
      }
      
      container.scrollTo({
        left: Math.max(0, targetScrollLeft),
        behavior: 'smooth'
      });
      
      // Reset the flag after scroll completes
      setTimeout(() => {
        isScrollingProgrammatically.current = false;
      }, 500); // Allow time for smooth scroll to complete
    }
  };

  const handleAccept = (itemId: string) => {
    // Increment the counter for this item to trigger animation
    setAcceptCounters(prev => ({
      ...prev,
      [itemId]: (prev[itemId] || 0) + 1
    }));
    
    // Call the parent handler first
    if (onItemAccept) {
      onItemAccept(itemId);
    }
    
    // Auto-progress to next slide if enabled (but not on the last slide)
    if (autoProgressOnAccept && currentIndex < items.length - 1) {
      setTimeout(() => {
        const nextIndex = currentIndex + 1;
        handleClick(nextIndex);
      }, 800); // Small delay to show the accepted state briefly
    }
  };

  const handleUnaccept = (itemId: string) => {
    // Call the parent handler
    if (onItemUnaccept) {
      onItemUnaccept(itemId);
    }
    // Note: No auto-progress on unaccept - user stays on current slide
  };

  return (
    <div className={`relative w-full h-full overflow-hidden ${className}`}>
      <style>{`
        @keyframes checkmarkPop {
          0% {
            transform: scale(0) rotate(-180deg);
            opacity: 0;
          }
          70% {
            transform: scale(1.1) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: scale(1) rotate(0deg);
            opacity: 1;
          }
        }
      `}</style>
      <div 
        ref={scrollContainerRef}
        className="w-full h-full flex flex-row items-center justify-start px-[295px] overflow-x-auto snap-x snap-mandatory"
        style={{
          scrollBehavior: 'smooth',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {items.map((item, index) => {
          const isActive = index === currentIndex;
          const isAccepted = acceptedItems.has(item.id);
          const width = isActive ? 680 : 590;
          const height = isActive ? '100%' : '90%';
          const opacity = isActive ? 1.0 : 0.5;
          
          // Check if this is the first unaccepted item
          const firstUnacceptedIndex = items.findIndex(i => !acceptedItems.has(i.id));
          const isFirstUnaccepted = index === firstUnacceptedIndex;
          
          return (
            <div
              key={item.id}
              className={`flex-shrink-0 ${isActive ? 'rounded-[2.5rem]' : 'rounded-3xl'} cursor-pointer relative transition-all duration-300 ease-out snap-center ${isActive ? '' : 'backdrop-blur-xl'} ${
                index > 0 ? 'ml-10' : ''
              }`}
              style={{
                backgroundImage: `url(${item.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                width: `${width}px`,
                height: height,
                opacity,
              }}
              onClick={() => {
                if (isActive && showAcceptButton && !isAccepted && onItemAccept) {
                  // If active and unaccepted, accept the item
                  handleAccept(item.id);
                } else if (isActive && showAcceptButton && isAccepted && onItemUnaccept) {
                  // If active and accepted, unaccept the item
                  handleUnaccept(item.id);
                } else {
                  // Otherwise, navigate to this slide
                  handleClick(index);
                }
              }}
            >
              {/* Gradient Overlay - Only show when active */}
              {isActive && (
                <div 
                  className="absolute inset-0 rounded-[2.5rem]"
                  style={{
                    background: `linear-gradient(to bottom, rgba(0,0,0,0) 62.744%, rgba(0,0,0,0.8) 104.33%)`,
                  }}
                />
              )}
              
              {/* Title and Status - Only show when active */}
              {isActive && (
                <div className="absolute bottom-[40px] left-[40px] right-[40px] text-center">
                  {/* Reserved space for checkmark to prevent content shift */}
                  <div className="flex justify-center mb-4 h-12">
                    {/* Animated Checkmark - Only show when accepted */}
                    {showAcceptButton && isAccepted && (
                      <div 
                        key={`${item.id}-checkmark-${acceptCounters[item.id] || 0}`}
                        className="bg-brand-background rounded-full w-12 h-12 flex items-center justify-center"
                        style={{
                          animation: 'checkmarkPop 0.4s ease-out forwards'
                        }}
                      >
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--brand-primary)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20,6 9,17 4,12"></polyline>
                        </svg>
                      </div>
                    )}
                  </div>
                  
                  {/* Heading */}
                  <h3 className="font-brand-primary text-white tracking-[-1.2px] text-[45px] leading-[50px] mb-4">
                    {item.title}
                  </h3>
                  
                  {/* Subtitle Text - consistent height */}
                  <div className="h-6 flex items-center justify-center">
                    {showAcceptButton && !isAccepted && (
                      <p 
                        className={`font-brand-body text-white/60 font-light text-brand-background/80 text-[22px] leading-[28px] ${
                          isActive && isFirstUnaccepted ? 'animate-pulse' : ''
                        }`}
                      >
                        tap to accept
                      </p>
                    )}                                     
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CarouselSnap;
